/*
 * seo.js — canonical, per-page SEO copy for LIFTILE.
 * Keep language natural, keyword-rich but not stuffed.
 * All copy is optimized for Google + AI answer engines (Google AI Overviews, ChatGPT, Gemini, Claude, Perplexity, Copilot).
 */

export const PAGE_SEO = {
  home: {
    title: "Construction Lifting Equipment Manufacturer in India",
    description:
      "LIFTILE is India's premium manufacturer of construction lifting equipment — tower hoists, passenger cum material hoists, suspended platforms, monkey lifts, bar bending & cutting machines and concrete mixers. Trusted by 600+ customers across India.",
    keywords:
      "construction lifting equipment, tower hoist manufacturer India, passenger cum material hoist, rope suspended platform, monkey lift, bar bending machine, concrete mixer, LIFTILE, construction machinery India, hoist manufacturer Vadodara",
    path: "/",
    image: "/product-passenger-hoist.jpg",
  },
  about: {
    title: "About LIFTILE — India's Trusted Construction Machinery Manufacturer",
    description:
      "LIFTILE Machinery Pvt. Ltd. designs and manufactures premium construction lifting equipment in Vadodara, Gujarat. In-house engineering, ISO-standard fabrication and owner-inspected quality across every hoist, platform and mixer we ship.",
    keywords:
      "about LIFTILE, construction machinery manufacturer India, Vadodara hoist factory, LIFTILE Machinery Pvt Ltd, tower hoist manufacturer Gujarat, LIFTILE founder Jayantibhai Tarabdamali",
    path: "/about",
    image: "/about-header-factory.png",
  },
  productsList: {
    title: "Construction Machinery Range — Tower Hoists, Platforms, Mixers & More",
    description:
      "Explore LIFTILE's complete product range: conventional & multi-functional tower hoists, passenger cum material hoists, rope suspended platforms, monkey lifts, bar bending & cutting machines, concrete mixers and wheel barrows.",
    keywords:
      "LIFTILE products, construction machinery catalogue India, tower hoist, passenger hoist, rope suspended platform, bar bender, bar cutter, concrete mixer, monkey lift",
    path: "/products",
    image: "/liftile-factory.jpg",
  },
  projects: {
    title: "Projects & Case Studies — LIFTILE Machines on Live Construction Sites",
    description:
      "See LIFTILE construction equipment in action across residential towers, refineries, power plants and infrastructure sites in Kerala, Gujarat, Rajasthan, Chhattisgarh and beyond.",
    keywords:
      "LIFTILE projects, hoist installation projects India, tower hoist case studies, IOCL refinery hoist, Adani power plant hoist, high-rise construction machinery",
    path: "/projects",
    image: "/project-adani-korba.png",
  },
  dealer: {
    title: "Become a LIFTILE Dealer — Construction Machinery Distribution Partnership",
    description:
      "Join LIFTILE's growing dealer network across India. Attractive margins, marketing support, engineering training and a premium product portfolio backed by a nationwide service network.",
    keywords:
      "LIFTILE dealer, construction machinery distributor India, hoist dealer network, become a construction equipment dealer India",
    path: "/dealer",
    image: "/liftile-factory.jpg",
  },
  insights: {
    title: "Construction Insights — Guides, Comparisons & Buying Tips",
    description:
      "LIFTILE's Construction Insights knowledge center: expert guides on choosing tower hoists, passenger cum material hoists, rebar machines and concrete mixers. Buying tips, safety guidance and technical explainers for builders and project managers.",
    keywords:
      "construction machinery guide, tower hoist buying guide, passenger hoist selection, rebar machine guide, concrete mixer selection, LIFTILE insights",
    path: "/insights",
    image: "/liftile-factory.jpg",
  },
  contact: {
    title: "Contact LIFTILE — Get a Quote, Visit Our Factory or Speak With Engineering",
    description:
      "Contact LIFTILE Machinery Pvt. Ltd. in Vadodara, Gujarat for quotes, engineering consultation, spare parts and after-sales support. Call +91 8866939091 or email info@liftile.com.",
    keywords:
      "contact LIFTILE, construction machinery quote India, Vadodara factory address, LIFTILE phone number, LIFTILE email, hoist quote India",
    path: "/contact",
    image: "/about-header-factory.png",
  },
};

/* Per-product SEO copy keyed by slug. */
export const PRODUCT_SEO = {
  "conventional-tower-hoist": {
    title: "Conventional Tower Hoist — 500 kg / Up to 60 m | LIFTILE",
    description:
      "LIFTILE Conventional Tower Hoist for mid-rise construction. 500 kg capacity, up to 60 m lifting height, dual safety brake, modular mast sections, IS 3696 compliant. Made in Vadodara, India.",
    keywords:
      "conventional tower hoist India, tower hoist manufacturer, 500 kg material hoist, IS 3696 tower hoist, LIFTILE conventional hoist",
  },
  "multi-functional-tower-hoist": {
    title: "Multi Functional Tower Hoist — 800 kg / Up to 100 m | LIFTILE",
    description:
      "LIFTILE Multi Functional Tower Hoist for high-rise material & light personnel transport. 800 kg capacity, up to 100 m height, VFD control, SAJ safety device, EN 12158 / IS 3696 compliant.",
    keywords:
      "multi functional tower hoist India, dual mode hoist, personnel and material hoist, VFD tower hoist, LIFTILE MFT hoist",
  },
  "passenger-cum-material-hoist": {
    title: "Passenger Cum Material Hoist — 2000 kg / 150 m | LIFTILE LPMH",
    description:
      "LIFTILE LPMH Series Passenger Cum Material Hoist — 1000/1500/2000 kg twin-cage capacity, up to 150 m lifting height, rack-and-pinion drive, SAJ 40-1.2 safety device, EN 12158 compliant.",
    keywords:
      "passenger cum material hoist India, twin cage construction hoist, rack and pinion hoist, LPMH2000, LIFTILE LPMH hoist, 150m construction hoist",
  },
  "rope-suspended-platform": {
    title: "Rope Suspended Platform — Facade Access up to 130 m | LIFTILE LRP10X",
    description:
      "LIFTILE LRP10X Rope Suspended Platform for facade, painting, glass and maintenance access. Modular 1.5–7.5 m platforms, dual wire rope safety, hot dip galvanized structure, EN 1808 compliant.",
    keywords:
      "rope suspended platform India, LRP10X, facade access platform, working platform manufacturer, LIFTILE suspended platform, EN 1808 platform",
  },
  "monkey-lift": {
    title: "Monkey Lift (Mini Jib Crane) — Slab-Level Material Handling | LIFTILE",
    description:
      "LIFTILE Monkey Lift / Mini Jib Crane for slab-level material movement. 200–500 kg capacity, compact footprint, easy slab mounting, single/three-phase operation.",
    keywords:
      "monkey lift India, mini jib crane, slab crane, floor to floor lift, LIFTILE monkey lift, construction slab lifter",
  },
  "bar-bending-machine": {
    title: "Bar Bending Machine — Up to 42 mm Rebar | LIFTILE",
    description:
      "LIFTILE Bar Bending Machine for high-volume rebar workshops and precast plants. Bends bars up to 42 mm at 0°–180° programmable angles, 3 kW motor, foot-pedal control.",
    keywords:
      "bar bending machine India, rebar bender 42mm, TMT bending machine, LIFTILE bar bender, precast rebar equipment",
  },
  "bar-cutting-machine": {
    title: "Bar Cutting Machine — Up to 50 mm Rebar | LIFTILE",
    description:
      "LIFTILE Bar Cutting Machine for clean, fast cuts on high-tensile rebar up to 50 mm. Shock-resistant frame, oil-lubricated drive, 4 kW motor, 32 cuts/min.",
    keywords:
      "bar cutting machine India, rebar cutter 50mm, TMT cutting machine, LIFTILE bar cutter, rebar shear",
  },
  "concrete-mixer": {
    title: "Concrete Mixer 10/7 — Heavy-Duty Site Mixer | LIFTILE",
    description:
      "LIFTILE 10/7 cu.ft Concrete Mixer with hopper feed. Heavy-duty drum, robust gear drive, wheel-mounted mobility, choice of diesel or 5 HP / 7.5 HP electric motor.",
    keywords:
      "concrete mixer India, 10/7 mixer, hopper concrete mixer, LIFTILE concrete mixer, drum mixer manufacturer",
  },
  "reversible-drum-concrete-mixer": {
    title: "Reversible Drum Concrete Mixer RD 8000 — 8 m³/hr | LIFTILE",
    description:
      "LIFTILE RD 8000 Reversible Drum Concrete Mixer — 800/560 L drum, 15 HP motor, hopper-fed loading, reverse discharge, up to 8 m³/hr output. Ideal for high-rise, bridges and slab pours.",
    keywords:
      "reversible drum mixer India, RD 8000, reversible concrete mixer, hopper mixer, LIFTILE RD 8000, 8 cubic meter concrete mixer",
  },
  "wheel-barrow": {
    title: "Wheel Barrow LT1X / LT2X — Heavy-Duty Site Transport | LIFTILE",
    description:
      "LIFTILE LT1X single-wheel and LT2X double-wheel barrows for daily construction site material transport. 16-gauge steel tray, pneumatic tyres, powder-coated finish.",
    keywords:
      "construction wheel barrow India, LT1X wheel barrow, LT2X wheel barrow, LIFTILE wheel barrow, pneumatic tyre wheel barrow",
  },
};
